// The details of a crime map area as HTML, shared by the panel of the crime map and the crime
// profile widget of a place, so that both always say the same.

interface CrimeCategoryRow {
    readonly category: string;
    readonly incidentCount: number;
    readonly ratePer1000: number | null;
    readonly indexValue: number | null;
}

interface CrimeTrendPoint {
    readonly periodFrom: string;
    readonly periodTo: string;
    readonly incidentCount: number;
    readonly ratePer1000: number | null;
}

export interface CrimeArea {
    readonly level: string;
    readonly unitCode: string;
    readonly name: string;
    readonly townName: string | null;
    readonly periodFrom: string;
    readonly periodTo: string;
    readonly incidentCount: number;
    readonly population: number | null;
    readonly ratePer1000: number | null;
    readonly smoothedRatePer1000: number | null;
    readonly indexValue: number | null;
    readonly crimeClass: number | null;
    readonly reliability: string;
    readonly townRatePer1000: number | null;
    readonly districtRatePer1000: number | null;
    readonly countryRatePer1000: number | null;
    readonly policeIndex: number | null;
    readonly districtPoliceIndex: number | null;
    readonly countryPoliceIndex: number | null;
    readonly cadastralUnitName: string | null;
    readonly cadastralUnitRatePer1000: number | null;
    readonly referenceLevel: string | null;
    readonly referenceName: string | null;
    readonly referenceRatePer1000: number | null;
    readonly allIncidentCount: number;
    readonly areaKm2: number;
    readonly incidentsPerKm2: number | null;
    readonly referenceIncidentsPerKm2: number | null;
    readonly categories: readonly CrimeCategoryRow[];
    readonly trend: readonly CrimeTrendPoint[];
    readonly geometry: GeoJSON.Geometry;
}

// Five fixed classes of the crime index (country = 100), from very low to high: green through
// yellow to dark red, so that the lowest values are as visible over the light base map as the
// highest ones.
const CLASS_COLORS: Record<number, string> = {
    1: '#8fcf8a',
    2: '#d9e07a',
    3: '#fdc95c',
    4: '#f0632e',
    5: '#bd0026',
};
const CLASS_LABELS: Record<number, string> = {
    1: 'velmi nízká (do 50)',
    2: 'nízká (50–80)',
    3: 'průměrná (80–125)',
    4: 'zvýšená (125–200)',
    5: 'vysoká (nad 200)',
};
const CLASS_UPPER_BOUNDS = [50, 80, 125, 200];

// Continuous colour of the index: the class colours are the stops of a gradient, so an index of
// 199 and 201 look alike while 126 and 199 do not.
const COLOR_STOPS: ReadonlyArray<readonly [number, string]> = [
    [0, CLASS_COLORS[1]],
    [50, CLASS_COLORS[2]],
    [80, CLASS_COLORS[3]],
    [125, CLASS_COLORS[4]],
    [200, CLASS_COLORS[5]],
    [260, '#7a0019'],
];

// An area without inhabitants has no index of its own: it is measured by how much happens there
// per km² and year, in five classes with fixed bounds for the whole country, painted with the
// same colours as the index. The bounds come from the distribution of the uninhabited units
// (2026-09-09): roughly 70 % under 1, the top 1 % over 50.
const NO_POPULATION_RELIABILITY = 'no_population';
const CADASTRAL_UNIT_LEVEL = 'cadastral_unit';
const DENSITY_CLASS_UPPER_BOUNDS = [1, 3, 10, 50];
const DENSITY_CLASS_LABELS: Record<number, string> = {
    1: 'velmi nízká (do 1 na km²)',
    2: 'nízká (1–3 na km²)',
    3: 'průměrná (3–10 na km²)',
    4: 'zvýšená (10–50 na km²)',
    5: 'vysoká (nad 50 na km²)',
};
// Where the density scale ends on the colour ramp: the top class runs from 50 to this many per km².
const DENSITY_GAUGE_MAXIMUM = 250;
// Counts of an area are shares of anonymisation zones and come in fractions; below one whole
// incident over the period there is nothing to show but "none".
const MINIMUM_WHOLE_INCIDENTS = 1;

export const CATEGORY_LABELS: Record<string, string> = {
    violent: 'Násilná trestná činnost',
    burglary: 'Krádeže vloupáním',
    vehicle_theft: 'Krádeže vozidel a věcí z nich',
    theft: 'Ostatní krádeže',
    drugs: 'Drogová kriminalita',
    property_offence: 'Přestupky proti majetku',
    public_order_offence: 'Přestupky proti veřejnému pořádku',
    other: 'Ostatní kriminalita',
};

// Categories that form the index are listed first in the details.
const INDEX_CATEGORIES = ['violent', 'burglary', 'vehicle_theft', 'theft'];

// Tooltips of the category rows: what an incident is and why the rows do not add up to the total.
const INDEX_CATEGORY_TITLE = 'Kategorie indexu kriminality. Skutek je jeden případ; spadá-li do více kategorií, počítá se v každé z nich, v celkovém počtu a v indexu jen jednou.';
const BREAKDOWN_CATEGORY_TITLE = 'Do indexu kriminality nevstupuje. Skutek spadající do více kategorií se počítá v každé z nich, v celkovém počtu jen jednou.';
const INDEX_TREND_TITLE = 'Jen skutky v kategoriích indexu kriminality.';

const RELIABILITY_NOTES: Record<string, string> = {
    insufficient_data: 'Málo skutků pro vlastní index; hodnota je odhad opřený o průměr okolí (katastrální území nebo obec).',
    no_population: 'Oblast nemá (téměř) žádné obyvatele, index na obyvatele by tu nic neříkal. Barva na mapě je podle množství skutků na km² a rok, čísla níže jsou počty skutků.',
};

const ATTRIBUTION = 'Zdroj: Policie ČR, ČSÚ (SLDB 2021), ČÚZK';

function hexToRgb(hex: string): [number, number, number] {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

export function colorForIndex(index: number): string {
    const value = Math.max(0, Math.min(index, COLOR_STOPS[COLOR_STOPS.length - 1][0]));
    for (let position = 1; position < COLOR_STOPS.length; position++) {
        const [upper, upperColor] = COLOR_STOPS[position];
        if (value > upper) {
            continue;
        }
        const [lower, lowerColor] = COLOR_STOPS[position - 1];
        const share = (value - lower) / (upper - lower);
        const from = hexToRgb(lowerColor);
        const to = hexToRgb(upperColor);
        const mixed = from.map((channel, index) => Math.round(channel + (to[index] - channel) * share));
        return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
    }
    return COLOR_STOPS[COLOR_STOPS.length - 1][1];
}

function gradientCss(): string {
    const maximum = COLOR_STOPS[COLOR_STOPS.length - 1][0];
    return `linear-gradient(to right, ${COLOR_STOPS.map(([stop, color]) => `${color} ${(stop / maximum * 100).toFixed(1)}%`).join(', ')})`;
}

// The index of the reference of the area (its cadastral unit where that is a neighbourhood
// around it, otherwise its town).
function referenceIndex(area: CrimeArea): number | null {
    if (area.referenceRatePer1000 !== null && area.countryRatePer1000 !== null && area.countryRatePer1000 > 0) {
        return area.referenceRatePer1000 / area.countryRatePer1000 * 100;
    }
    return null;
}

// "KÚ Hostivař" or "obec Most": the reference named the way the panel speaks of it.
function referenceLabel(area: CrimeArea): string {
    const kind = area.referenceLevel === CADASTRAL_UNIT_LEVEL ? 'KÚ' : 'obec';
    return area.referenceName !== null ? `${kind} ${escapeHtml(area.referenceName)}` : kind;
}

// Whether the area shows the index of its reference instead of its own: nobody lives there, so a
// rate per inhabitant says nothing about the place.
export function borrowsReferenceIndex(area: CrimeArea): boolean {
    return area.level !== 'town' && (area.reliability === NO_POPULATION_RELIABILITY || area.indexValue === null) && referenceIndex(area) !== null;
}

// Whether at least one whole incident landed in the area over the period.
export function hasWholeIncidents(area: CrimeArea): boolean {
    return area.allIncidentCount >= MINIMUM_WHOLE_INCIDENTS;
}

// The incident density of an area nobody lives in; zero below one whole incident.
export function incidentDensity(area: CrimeArea): number | null {
    if (!hasWholeIncidents(area)) {
        return 0;
    }
    return area.incidentsPerKm2;
}

function densityClass(density: number): number {
    const position = DENSITY_CLASS_UPPER_BOUNDS.findIndex((bound) => density < bound);
    return position === -1 ? DENSITY_CLASS_UPPER_BOUNDS.length + 1 : position + 1;
}

// The density laid onto the colour ramp of the index: each density class spans the same stretch
// of the ramp as the index class of the same number, so that the same colour means the same
// class on both scales.
function densityAsIndexScale(density: number): number {
    const indexBounds = [0, ...CLASS_UPPER_BOUNDS, GAUGE_MAXIMUM];
    const densityBounds = [0, ...DENSITY_CLASS_UPPER_BOUNDS, DENSITY_GAUGE_MAXIMUM];
    const value = Math.min(density, DENSITY_GAUGE_MAXIMUM);
    for (let position = 1; position < densityBounds.length; position += 1) {
        if (value <= densityBounds[position]) {
            const share = (value - densityBounds[position - 1]) / (densityBounds[position] - densityBounds[position - 1]);
            return indexBounds[position - 1] + share * (indexBounds[position] - indexBounds[position - 1]);
        }
    }
    return GAUGE_MAXIMUM;
}

// The value to paint on the index colour ramp: the area's own index, or its incident density
// where nobody lives there (the reference's index where even that is unknown).
export function paintedIndex(area: CrimeArea): number | null {
    if (!borrowsReferenceIndex(area)) {
        return area.indexValue;
    }
    const density = incidentDensity(area);
    return density !== null ? densityAsIndexScale(density) : referenceIndex(area);
}

function classForIndex(index: number): number {
    const position = CLASS_UPPER_BOUNDS.findIndex((bound) => index < bound);
    return position === -1 ? CLASS_UPPER_BOUNDS.length + 1 : position + 1;
}

export function formatNumber(value: number, digits: number): string {
    return value.toLocaleString('cs-CZ', {minimumFractionDigits: digits, maximumFractionDigits: digits});
}

export function formatDate(isoDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!match) {
        return isoDate;
    }
    return `${Number(match[3])}. ${Number(match[2])}. ${match[1]}`;
}

function formatPeriod(from: string, to: string): string {
    return `${formatDate(from)} – ${formatDate(to)}`;
}

export function escapeHtml(value: string): string {
    return value.replace(/[&<>"]/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
    })[character] ?? character);
}

// "obec −35 %" — how the area's value compares with a reference value, as a coloured chip.
function comparisonChip(label: string, rate: number, reference: number | null, unit = 'skutků na 1 000 obyvatel'): string | null {
    if (reference === null || reference <= 0) {
        return null;
    }
    // A tiny value next to a large reference rounds to −100 %, which reads as "nothing at all";
    // −99 % says "almost nothing" and stays true.
    const difference = Math.max(-99, Math.round((rate / reference - 1) * 100));
    const tone = difference > 5 ? 'worse' : difference < -5 ? 'better' : 'same';
    const value = difference === 0 ? '±0 %' : `${difference > 0 ? '+' : '−'}${Math.abs(difference)} %`;
    return `<span class="crime-details__chip crime-details__chip--${tone}" title="${label}: ${formatNumber(reference, 1)} ${unit}">`
        + `<span class="crime-details__chip-label">${label}</span><span class="crime-details__chip-value">${value}</span></span>`;
}

// Where the index sits on the five-class scale: a bar of the class colours with a marker.
const GAUGE_MAXIMUM = 260;

// Ticks of the index scale: the class bounds and the country.
function indexTicks(): Array<[number, string]> {
    return [[100, 'ČR'], ...CLASS_UPPER_BOUNDS.map((bound): [number, string] => [bound, String(bound)])];
}

// Ticks of the density scale, placed where its class bounds fall on the index ramp.
function densityTicks(): Array<[number, string]> {
    return DENSITY_CLASS_UPPER_BOUNDS.map((bound): [number, string] => [densityAsIndexScale(bound), String(bound)]);
}

function gaugeTicks(ticks: Array<[number, string]>): string {
    const spans = ticks.map(([position, label]) => `<span class="crime-details__gauge-tick" style="left:${(position / GAUGE_MAXIMUM * 100).toFixed(2)}%">${label}</span>`);
    return `<div class="crime-details__gauge-ticks">${spans.join('')}</div>`;
}

function indexGauge(indexValue: number, ticks: Array<[number, string]> = indexTicks()): string {
    const marker = Math.min(indexValue, GAUGE_MAXIMUM) / GAUGE_MAXIMUM * 100;
    return `<div class="crime-details__gauge"><div class="crime-details__gauge-bar" style="background:${gradientCss()}">`
        + `<span class="crime-details__gauge-marker" style="left:${marker.toFixed(2)}%"></span></div>${gaugeTicks(ticks)}</div>`;
}

// The yearly slices: rates per 1 000 inhabitants, or plain counts where the area borrows its index.
export function trendBars(area: CrimeArea): string {
    const counts = borrowsReferenceIndex(area);
    const points = area.trend.filter((point) => counts || point.ratePer1000 !== null);
    if (points.length < 2) {
        return '';
    }
    // Counts are shown and scaled as whole numbers, so that three fractions of an incident do not
    // draw three full bars labelled zero.
    const valueOf = (point: CrimeTrendPoint): number => (counts ? Math.round(point.incidentCount) : point.ratePer1000 as number);
    const digits = counts ? 0 : 1;
    const unit = counts ? 'skutků' : 'na 1 000 obyv.';
    const maximum = Math.max(...points.map(valueOf));
    if (maximum <= 0) {
        return '';
    }
    const bars = points.map((point) => {
        const value = valueOf(point);
        const height = Math.max(4, Math.round(value / maximum * 100));
        const year = point.periodTo.slice(0, 4);
        return `<div class="crime-details__bar${counts ? ' crime-details__bar--count' : ''}" title="${formatPeriod(point.periodFrom, point.periodTo)}: ${formatNumber(value, digits)} ${unit}">`
            + `<span class="crime-details__bar-value">${formatNumber(value, digits)}</span>`
            + `<span class="crime-details__bar-fill" style="height:${height}%"></span>`
            + `<span class="crime-details__bar-label">${year}</span></div>`;
    });
    const heading = counts ? `<div class="crime-details__meta" title="${INDEX_TREND_TITLE}">Skutků po letech</div>` : '<div class="crime-details__meta">Skutků na 1 000 obyvatel po letech</div>';
    return `<div class="crime-details__trend">${heading}`
        + `<div class="crime-details__bars">${bars.join('')}</div></div>`;
}

export function categoriesTable(area: CrimeArea): string {
    if (area.categories.length === 0) {
        return '';
    }
    const ordered = [...area.categories].sort((a, b) => {
        const aIndex = INDEX_CATEGORIES.includes(a.category) ? 0 : 1;
        const bIndex = INDEX_CATEGORIES.includes(b.category) ? 0 : 1;
        return aIndex - bIndex || b.incidentCount - a.incidentCount;
    });
    const counts = borrowsReferenceIndex(area);
    const rows = ordered.map((row) => {
        const label = escapeHtml(CATEGORY_LABELS[row.category] ?? row.category);
        const rate = !counts && row.ratePer1000 !== null ? `${formatNumber(row.ratePer1000, 1)} / 1 000 obyv.` : `${formatNumber(row.incidentCount, 0)} skutků`;
        const inIndex = INDEX_CATEGORIES.includes(row.category);
        const modifier = inIndex ? '' : ' crime-details__category--breakdown';
        const title = inIndex ? INDEX_CATEGORY_TITLE : BREAKDOWN_CATEGORY_TITLE;
        return `<tr class="crime-details__category${modifier}" title="${title}"><td>${label}</td><td class="text-end">${rate}</td></tr>`;
    });
    return `<table class="crime-details__categories">${rows.join('')}</table>`;
}

// The name of the area, its town and the period.
export function detailsHead(area: CrimeArea): string {
    const levelLabel = area.level === 'town' ? 'obec' : 'základní sídelní jednotka';
    const town = area.townName && area.level !== 'town' ? `<span class="crime-details__town">${escapeHtml(area.townName)}</span>` : '';
    return `<div class="crime-details__head"><div class="crime-details__name">${escapeHtml(area.name)}</div>${town}`
        + `<div class="crime-details__meta">${levelLabel} · ${formatPeriod(area.periodFrom, area.periodTo)}</div></div>`;
}

// The hero figure with its class and scale: the index of the area, the index of its reference where
// it borrows one, or the incident density where nobody lives there; empty where there is none.
export function detailsHero(area: CrimeArea): string {
    const index = paintedIndex(area);
    if (borrowsReferenceIndex(area) && index !== null) {
        const color = colorForIndex(index);
        const density = incidentDensity(area);
        if (density !== null) {
            // Nobody lives here: what happens per km² and year, in the density classes.
            return `<div class="crime-details__hero" style="--crime-class-color:${color}">`
                + `<div class="crime-details__index"><span class="crime-details__index-value">${formatNumber(density, density < 10 ? 1 : 0)}</span>`
                + `<span class="crime-details__index-caption">skutků na km² a rok<br/>oblast bez obyvatel</span></div>`
                + `<div class="crime-details__class">${hasWholeIncidents(area) ? DENSITY_CLASS_LABELS[densityClass(density)] : 'bez skutků'}</div>`
                + `${indexGauge(index, densityTicks())}</div>`;
        }
        return `<div class="crime-details__hero" style="--crime-class-color:${color}">`
            + `<div class="crime-details__index"><span class="crime-details__index-value">${formatNumber(index, 0)}</span>`
            + `<span class="crime-details__index-caption">index ${referenceLabel(area)}<br/>ČR = 100</span></div>`
            + `<div class="crime-details__class">${CLASS_LABELS[classForIndex(index)]}</div>`
            + `${indexGauge(index)}</div>`;
    }
    if (area.indexValue !== null && area.crimeClass !== null) {
        const color = colorForIndex(area.indexValue);
        return `<div class="crime-details__hero" style="--crime-class-color:${color}">`
            + `<div class="crime-details__index"><span class="crime-details__index-value">${formatNumber(area.indexValue, 0)}</span>`
            + `<span class="crime-details__index-caption">index kriminality<br/>ČR = 100</span></div>`
            + `<div class="crime-details__class">${CLASS_LABELS[area.crimeClass]}</div>`
            + `${indexGauge(area.indexValue)}</div>`;
    }
    return '';
}

// The rate per inhabitant compared with the surroundings, or plain counts and the density where the
// area borrows its index.
export function detailsRates(area: CrimeArea): string {
    const parts: string[] = [];
    const borrowed = borrowsReferenceIndex(area);
    const rate = borrowed ? null : area.smoothedRatePer1000 ?? area.ratePer1000;
    if (borrowed) {
        // No rate per inhabitant: how much happens here in plain counts, and how dense it is per km²
        // next to the reference, the only comparison that makes sense for an industrial estate or a forest.
        const facts = `${area.population !== null ? `${formatNumber(area.population, 0)} obyvatel · ` : ''}${formatNumber(area.areaKm2, 1)} km² · bez přepočtu na obyvatele`;
        if (!hasWholeIncidents(area)) {
            parts.push(`<div class="crime-details__rate">Za období tu policie neeviduje žádný skutek.<div class="crime-details__meta">${facts}</div></div>`);
        } else {
            parts.push(`<div class="crime-details__rate"><strong>${formatNumber(area.allIncidentCount, 0)}</strong> skutků za období`
                + `<div class="crime-details__meta">${facts}</div></div>`);
            if (area.incidentsPerKm2 !== null) {
                const reference = area.referenceIncidentsPerKm2 !== null ? ` · ${referenceLabel(area)} ${formatNumber(area.referenceIncidentsPerKm2, 1)}` : '';
                parts.push(`<div class="crime-details__rate"><strong>${formatNumber(area.incidentsPerKm2, 1)}</strong> skutků na km² a rok`
                    + `<div class="crime-details__meta">${formatNumber(area.allIncidentCount, 0)} skutků za období · ${formatNumber(area.areaKm2, 1)} km²${reference}</div></div>`);
            }
        }
    }
    if (rate !== null) {
        const chips = [
            area.level === 'town' ? null : comparisonChip('KÚ', rate, area.cadastralUnitRatePer1000),
            area.level === 'town' ? null : comparisonChip('obec', rate, area.townRatePer1000),
            comparisonChip('okres', rate, area.districtRatePer1000),
            comparisonChip('ČR', rate, area.countryRatePer1000),
        ].filter((chip): chip is string => chip !== null);
        parts.push(`<div class="crime-details__rate"><strong>${formatNumber(rate, 1)}</strong> skutků na 1 000 obyvatel a rok`
            + `<div class="crime-details__meta">${formatNumber(area.incidentCount, 0)} skutků za období`
            + `${area.population !== null ? `, ${formatNumber(area.population, 0)} obyvatel` : ''}</div>`
            + (chips.length > 0 ? `<div class="crime-details__chips">${chips.join('')}</div>` : '')
            + `</div>`);
    }
    return parts.join('');
}

export function detailsPoliceIndex(area: CrimeArea): string {
    const borrowed = borrowsReferenceIndex(area);
    const references = [
        area.districtPoliceIndex !== null ? `okres ${formatNumber(area.districtPoliceIndex, 0)}` : null,
        area.countryPoliceIndex !== null ? `ČR ${formatNumber(area.countryPoliceIndex, 0)}` : null,
    ].filter((reference): reference is string => reference !== null);
    // The Police publish their index for towns, districts and regions only; an area without
    // inhabitants shows just the references.
    if (borrowed && references.length > 0) {
        return `<div class="crime-details__police"><div><span class="crime-details__police-label">Index kriminality Policie ČR</span></div>`
            + `<div class="crime-details__meta">všechny trestné činy na 10 000 obyvatel a rok · ${references.join(' · ')}</div></div>`;
    }
    if (!borrowed && area.policeIndex !== null) {
        return `<div class="crime-details__police"><div><span class="crime-details__police-label">Index kriminality Policie ČR</span>`
            + `<span class="crime-details__police-value">${formatNumber(area.policeIndex, 0)}</span></div>`
            + `<div class="crime-details__meta">všechny trestné činy na 10 000 obyvatel a rok`
            + `${references.length > 0 ? ` · ${references.join(' · ')}` : ''}</div></div>`;
    }
    return '';
}

export function detailsNote(area: CrimeArea): string {
    const note = RELIABILITY_NOTES[area.reliability];
    return note ? `<div class="crime-details__note">${note}</div>` : '';
}

// Whether the yearly slices and the categories say anything: not for an area nobody lives in where
// not a single whole incident happened.
export function showsBreakdown(area: CrimeArea): boolean {
    return !borrowsReferenceIndex(area) || hasWholeIncidents(area);
}

export function detailsContent(area: CrimeArea): string {
    const breakdown = showsBreakdown(area);
    return [
        detailsHead(area),
        detailsHero(area),
        detailsRates(area),
        detailsPoliceIndex(area),
        detailsNote(area),
        breakdown ? trendBars(area) : '',
        breakdown ? categoriesTable(area) : '',
        attributionContent(),
    ].filter((part) => part !== '').join('');
}

// The colour key of the map, shown in the panel while no area is hovered.
export function legendContent(): string {
    const items = Object.keys(CLASS_COLORS).map(Number).map((crimeClass) => '<div class="crime-legend__item">'
        + `<span class="crime-legend__swatch" style="background:${CLASS_COLORS[crimeClass]}"></span>${CLASS_LABELS[crimeClass]}</div>`);
    return `<div class="crime-legend"><div class="crime-legend__title">Index kriminality (ČR = 100)</div>`
        + `<div class="crime-details__gauge"><div class="crime-details__gauge-bar" style="background:${gradientCss()}"></div>${gaugeTicks(indexTicks())}</div>`
        + `${items.join('')}<div class="crime-legend__note">Barva přechází plynule podle hodnoty. Kde je dat málo, opírá se index o katastrální území nebo obec, panel to u oblasti uvádí. `
        + `Obchodní zóny, nádraží, pole a lesy, kde nikdo nebydlí, mají barvu podle množství skutků na km² a rok (třídy do 1, 3, 10 a 50).</div></div>`;
}

export function attributionContent(): string {
    return `<div class="crime-details__attribution">${ATTRIBUTION}</div>`;
}
